import React from 'react';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { convertBytesToSize } from '@utils/fileSizeFormatter';
import { formatDate } from '@utils/kpi-utils/date';

import { ITitle } from '@interfaces/common/common';

import { formatTemplateWithTime, timezoneUTC } from '@constants/common';
import {
	attachFilesTooltip,
	attachFilesTooltipDisabled,
} from './constants';

interface IGetColumns {
	openDeleteConfirm: () => void;
	setFileIdForRemove: React.Dispatch<React.SetStateAction<string | null>>;
	onAttachFileClick: (id: string) => void;
	isDeleteAction: boolean;
	downloadFile: (fileId: string) => void;
	onMoveFileClick: (id: string) => void;
	isMoveAction: boolean;
	isAttachDisabled: boolean;
	isAttachAction: boolean;
}

export const getColumns = ({
	openDeleteConfirm,
	setFileIdForRemove,
	onAttachFileClick,
	isDeleteAction,
	downloadFile,
	onMoveFileClick,
	isMoveAction,
	isAttachDisabled,
	isAttachAction,
}: IGetColumns) => ([
	{
		header: 'Full Name',
		accessorKey: 'name',
		cell: ({ getValue }: CellContext<any, any>) => {
			const name = getValue();

			return <AdaptiveTableCell text={name} />;
		},
	},
	{
		header: 'Size',
		accessorKey: 'size',
		cell: ({ getValue }: CellContext<any, any>) => {
			const size = getValue();

			return <AdaptiveTableCell text={size ? convertBytesToSize(size) : ''} />;
		},
	},
	{
		header: 'Milestone',
		accessorKey: 'milestones',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value: Array<ITitle> = getValue();
			const milestones = value?.map(milestone => milestone?.title)?.join(', ');

			return (
				<AdaptiveTableCell text={milestones} />
			);
		},
	},
	{
		header: 'Created',
		accessorKey: 'createdAt',
		cell: ({ getValue }: CellContext<any, any>) => {
			const createdAt = getValue();
			const date = createdAt ? formatDate(utcToZonedTime(createdAt, timezoneUTC), formatTemplateWithTime) : '';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Updated',
		accessorKey: 'updatedAt',
		cell: ({ getValue }: CellContext<any, any>) => {
			const updatedAt = getValue();
			const date = updatedAt ? formatDate(utcToZonedTime(updatedAt, timezoneUTC), formatTemplateWithTime) : '';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Action',
		accessorKey: 'action',
		enableSorting: false,
		cell: ({ row }: CellContext<any, any>) => {
			const handleDelete = () => {
				setFileIdForRemove(row?.original?.id);
				openDeleteConfirm();
			};

			const handleDownload = () => {
				downloadFile(row?.original?.id);
			};

			const handleConvert = () => {
				onMoveFileClick(row?.original?.id);
			};

			const handleAttach = () => {
				onAttachFileClick(row?.original?.id);
			};

			return (
				<Actions
					attachTooltip={isAttachDisabled ? attachFilesTooltipDisabled : attachFilesTooltip}
					convertTooltip='Move file'
					deleteTooltip='Delete file'
					downloadHandler={handleDownload}
					downloadTooltip='Download file'
					handleAttach={handleAttach}
					handleConvert={handleConvert}
					handleDelete={handleDelete}
					isAttach={isAttachAction}
					isAttachDisabled={isAttachDisabled}
					isConvert={isMoveAction}
					isDelete={isDeleteAction}
					isDownload
				/>
			);
		},
	},
]);
